import React from 'react';
import { connect } from 'react-redux';
import { Form } from 'components/FormikForm';
import Password from 'components/partials/Fields/Password';
import SubmitCancelButtons from 'components/partials/SubmitCancelButtons';
import ContactInformation from 'components/UserAccount/Settings/ContactInformation';
import IdentityInformation from 'components/Register/_components/IdentityInformation';
import AgreementSection from 'components/Register/_components/AgreementSection';
import IdentityVerification from 'components/Register/_components/IdentityVerification';
import useRegisterForm from 'components/Register/_hooks/useRegisterForm';
import { validationSchema, initialValues } from 'components/Register/_helpers/validation';
import { GUEST } from 'Enums/middleware';
import {
    checkIfDuplicateEmail,
    checkIfDuplicatePhone,
    clearRegForm,
    clearThingamabobActions,
    doLogout,
    getHeardQuestions,
    getIDVerificationQuestions,
    getPMSSystems,
    getSecurityQuestions,
    handleRegInputChange,
    regFormSaveStep1,
    requestFormCSRF,
    setThingamabobActions,
} from 'actions';
import { APP_CONFIG } from 'Config';
import { FACILITY_MANAGER } from 'helpers/users';

const { REACT_APP_REQUIRE_IDENTITY } = APP_CONFIG;

const RegisterFormStep1 = (props) => {
    const regTypeId = Number(props.reg_type_id);
    const {
        loading,
        verification,
        questions,
        userId,
        getQuestions,
        lastStepRedirect,
        handleSubmit,
        handleCancelForm,
    } = useRegisterForm({...props, regTypeId});

    return (
        <div className="registration registration-contact-info marginB15">
            <h3 className="register__setup-account-title font-weight-bold mb-3">{props.pageTitle}</h3>
            {
                verification
                    ? <IdentityVerification
                        handleFormSubmit={handleSubmit}
                        questions={questions}
                        redirect={lastStepRedirect}
                        user_id={userId}
                        reg_type={regTypeId}
                        getQuestions={getQuestions}
                    />
                    : <Form
                        initialValues={initialValues}
                        validationSchema={validationSchema(regTypeId)}
                        onSubmit={handleSubmit}
                    >
                        {({ errors, dirty, touched }) => {
                            return (
                                <>
                                    <ContactInformation />
                                    <IdentityInformation
                                        showIdentityInfo={regTypeId !== FACILITY_MANAGER.id && REACT_APP_REQUIRE_IDENTITY}
                                    />
                                    <Password
                                        secretQuestion
                                        title='Account Password & Security'
                                        extraRules={false}
                                        padding
                                        heard={props.heard}
                                    />
                                    <AgreementSection
                                        errors={errors}
                                        touched={touched}
                                        dirty={dirty}
                                        regTypeId={regTypeId}
                                    />
                                    <SubmitCancelButtons
                                        saving={loading}
                                        loading={loading}
                                        cancel_type="registration"
                                        submitLabel={regTypeId === FACILITY_MANAGER.id ? 'Continue to Step 2' : 'Continue'}
                                        handleCancelForm={handleCancelForm}
                                    />
                                </>
                            );
                        }}
                    </Form>
            }
        </div>
    );
};

RegisterFormStep1.getInitialProps = async ({ store: { dispatch }, query: { type }, res }) => {
    let pageTitle = 'Set Up Account';
    let reg_type_id = '0';
    let invalid_type = false;

    switch (type) {
        case 'auction-buyer':
            reg_type_id = '2';
            break;
        case 'facility-manager':
            reg_type_id = '1';
            break;
        case 'private-seller':
            if (!APP_CONFIG.REACT_APP_ENABLE_PRIVATE_SELLERS) {
                res.writeHead(307, { Location: '/404' });
                res.end();
            }
            reg_type_id = '4';
            break;
        default:
            invalid_type = true;
    }

    let token = null;

    await Promise.all([
        dispatch(getPMSSystems()),
        // won't actually logout the user. But it will clear out cookies if something is left.
        dispatch(doLogout()),

        // on page load set the user reg_type
        dispatch(handleRegInputChange({ target: { name: 'reg_type_id', value: reg_type_id } }, 'user')),
        dispatch(handleRegInputChange({ target: { name: 'opt_in_email', value: '1' } }, 'user')),

        new Promise(async (resolve) => {
            const { csrf_token } = await dispatch(requestFormCSRF({ scope: 'reg-type' }));
            token = csrf_token;
            resolve();
        }),
    ]);

    const pageData = { 
        title: 'Account Registration', 
        h1: 'Create an Account', 
        marginBottom: 'pageTitle marginB20',
        description: undefined, 
    };

    return { type, invalid_type, pageTitle, reg_type_id, csrf_token: token, pageData };
};

const mapStateToProps = ({ registration, address, user }, props) => ({
    register: registration,
    heard: user.heard_questions,
    questions: user.security_questions,
    countries: address.countries,
    isDuplicateEmail: user.isDuplicateEmail,
    history: props.history,
});

const bindings = {
    handleRegInputChange,
    checkIfDuplicateEmail,
    checkIfDuplicatePhone,
    clearRegForm,
    regFormSaveStep1,
    setThingamabobActions,
    clearThingamabobActions,
    getPMSSystems,
    getHeardQuestions,
    getSecurityQuestions,
    getIDVerificationQuestions,
};

RegisterFormStep1.middleware = GUEST;
RegisterFormStep1.middleware_redirect = '/account/dashboard';

export default connect(mapStateToProps, bindings)(RegisterFormStep1);